import React, { useEffect, useState, useCallback } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import firebaseApp from '../../../firebase-app';
import { SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import { collection, doc, updateDoc } from 'firebase/firestore';
import svStrings from '../../../sv-strings';
import { useSnackbar } from 'notistack';
import { MappingTeams } from './mappings-list';

const styles: SxStyle = {
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

const MappingsListTeams = () => {
  const { strings } = useStringsContext();
  const [rows, setRows] = useState<MappingTeams[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const [data, loading, error] = useCollectionData(
    collection(firebaseApp.db, `mappings/teams/all`)
  );

  useEffect(() => {
    if (data && !loading && !error) {
      setRows(data as MappingTeams[]);
    }
  }, [data, error, loading]);

  const processRowUpdate = useCallback(
    async (updatedRow: any) => {
      try {
        await updateDoc(doc(firebaseApp.db, `mappings/teams/all/${updatedRow.id}`), updatedRow);
      } catch (error) {
        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
      return updatedRow;
    },
    [enqueueSnackbar, strings]
  );

  return (
    <DataGrid
      rows={rows}
      getRowId={(row) => row.abbrv}
      columns={columns}
      processRowUpdate={processRowUpdate}
      loading={loading}
      autoPageSize
    />
  );
};

const columns: GridColDef[] = [
  {
    field: 'abbrv',
    headerName: svStrings.mappings.teamAbbrv,
    width: 100,
    editable: false,
  },
  {
    field: 'name',
    headerName: svStrings.mappings.name,
    width: 180,
    editable: true,
  },
  {
    field: 'displayName',
    headerName: svStrings.mappings.displayName,
    width: 180,
    editable: true,
  },
  {
    field: 'sport',
    headerName: svStrings.mappings.sport,
    minWidth: 100,
    flex: 1,
    editable: true,
    type: 'singleSelect',
    valueOptions: ['FOOTBALL', 'FUTSAL'],
  },
  {
    field: 'gender',
    headerName: svStrings.mappings.gender,
    minWidth: 100,
    flex: 1,
    editable: true,
    type: 'singleSelect',
    valueOptions: ['men', 'women'],
  },
  {
    field: 'everySportId',
    headerName: svStrings.mappings.everySportId,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => value ?? '',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'fogisId',
    headerName: svStrings.mappings.fogisId,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => value ?? '',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'forzasysId',
    headerName: svStrings.mappings.forzasysId,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => value ?? '',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
  {
    field: 'smcId',
    headerName: svStrings.mappings.smcId,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (value) => value ?? '',
    minWidth: 100,
    flex: 1,
    editable: true,
  },
];

export default MappingsListTeams;
